// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, useTheme } from '@mui/system';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { PROJECTS_ROUTE } from 'Utils/routes';
import { InvalidateMapCoordinatesAction, SetMapFolderAction, SetShowMapviewAction } from 'Features/projectList/projectListActions';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'Hooks';
import ProjectListItem from 'Features/projectList/ProjectListItem';
import ProjectListRow from 'Features/projectList/ProjectListRow';
import ProjectsControls from 'Features/projectList/ProjectsSubheader/ProjectsControls';
import ProjectsMapToggle from 'Features/projectList/ProjectsMapToggle';
import { FOLDER_ORDER_SORT_FILTER_OPTIONS } from 'Features/projectList/projectViewOptions';
import { narrowProjects } from 'Features/projectList/filterProjects';
import { describeProjectsNotFound } from 'Features/projectList/projectsNotFound';
import { useRevealSelectedProject } from 'Features/projectList/useRevealSelectedProject';
import SearchField from 'Components/SearchField';
import FolderBreadcrumb from 'Components/FolderBreadcrumb';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { IAvailableProject, IAvailableProjectsResponse } from 'Utils/typings/state/folder';
import stylesDef from './styles';
import FolderName from '../FolderName';
import { findFolderById, selectFolderList, selectProjectsInFolder } from '../folderListSelector';
import { getAllProjectsInFolderThunk, getAvailableProjectsThunk } from '../folderListThunk';
import MoveProjectBulkDialog from './MoveProjectBulkDialog';
import { SetProjectsInFolderAction } from '../folderListActions';

const FolderDetails = () => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const { folderId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux variables
  const folderList = useAppSelector(
    selectFolderList,
  );

  const projects = useAppSelector(
    selectProjectsInFolder,
  );

  // Read from the store on every render so an inline rename shows immediately
  const folderDetails = findFolderById(folderList, folderId) ?? null;
  const [loading, setLoading] = useState<boolean>(false);
  const [showMoveProjectBulkDialog, setShowMoveProjectBulkDialog] = useState<boolean>(false);
  const [availableProjects, setAvailableProjects] = useState<IAvailableProject[]>([]);
  const [loadingAvailableProjects, setLoadingAvailableProjects] = useState<boolean>(false);
  // A folder's projects arrive whole, so searching, filtering and sorting happen here rather
  // than through the paginated dashboard's store - which this page must not disturb.
  const [keyword, setKeyword] = useState<string>('');
  const [sortFilter, setSortFilter] = useState<string>('updatedAtD');
  const [orderFilter, setOrderFilter] = useState<ProjectOrderSortFilter>('all');
  const [viewMode, setViewMode] = useState<ProjectViewMode>('list');

  // There is one map view, on the dashboard. Opening it from here hands it this folder, so it
  // arrives with the folder selected in its rail and only the folder's projects plotted -
  // rather than this page growing a second map of its own.
  const openFolderOnMap = () => {
    if (folderId) dispatch(SetMapFolderAction(folderId));
    dispatch(SetShowMapviewAction(true));
    navigate(PROJECTS_ROUTE);
  };

  const visibleProjects = useMemo(
    () => narrowProjects(projects, { keyword, orderFilter, sortFilter }),
    [projects, keyword, orderFilter, sortFilter],
  );

  // Leaving the map inside this folder lands here on the project whose pin was picked: its
  // row is scrolled into view and outlined, as the dashboard does for an unfiled project. The
  // hook is handed what is on screen - nothing while the fetch's loading state hides the list.
  const mapPickToReveal = useAppSelector((state) => state.projectList.mapPickToReveal);
  const listRef = useRef<HTMLDivElement>(null);
  const renderedProjects = useMemo(() => (loading ? [] : visibleProjects), [loading, visibleProjects]);
  useRevealSelectedProject({ listRef, projects: renderedProjects, sortKey: sortFilter });
  // An empty folder gets the onboarding card; a search or filter that matched nothing in a
  // folder that has projects gets the dashboard's not-found message instead.
  const folderIsEmpty = !loading && projects.length === 0;
  const nothingMatched = !loading && projects.length > 0 && visibleProjects.length === 0;
  const notFoundMessage = describeProjectsNotFound(orderFilter, keyword.trim());

  // useEffect
  useEffect(() => {
    if (!folderId) return;

    // Find the folder details from the folders list
    const folder = findFolderById(folderList, folderId);

    // If the folder has projects, fetch them
    if (folder?.projectCount) {
      getProjectsInFolder(folderId);
    }
  }, [folderId]);

  // Methods
  const getProjectsInFolder = async (id: string) => {
    setLoading(true);

    // Fetch projects in the folder
    await dispatch(getAllProjectsInFolderThunk(id));

    setLoading(false);
  };

  const handleMoveProjectToFolderSuccess = (project?: IProject) => {
    // Update the projects list with the new project
    const updatedProjects = projects.filter((p) => p._id !== project?._id);
    dispatch(SetProjectsInFolderAction(updatedProjects));
    // The dashboard's cached set still has this project in this folder; it refetches next time.
    dispatch(InvalidateMapCoordinatesAction());
  };

  const handleMoveProjectBulkOpen = async () => {
    setLoadingAvailableProjects(true);
    setShowMoveProjectBulkDialog(true);
    const availableProjects: IAvailableProjectsResponse = await dispatch(getAvailableProjectsThunk(folderId as string));
    setAvailableProjects(availableProjects.projects);
    setLoadingAvailableProjects(false);
  };

  // HTML
  return (
    <Box sx={styles.mapviewLayoutLeft}>
      {/* Breadcrumb */}
      <Box sx={styles.breadcrumbsContainer}>
        {/* No click handler: on the folder page All Projects is a link to the dashboard. */}
        <FolderBreadcrumb onAllProjectsClick={undefined}>
          <FolderName initialFolderName={folderDetails?.name} folderId={folderDetails?._id} />
        </FolderBreadcrumb>
        {
          projects.length > 0 && (
            <Button variant="contained" color="primary" sx={styles.addProjectsButton} onClick={handleMoveProjectBulkOpen}>
              Add Projects
            </Button>
          )
        }
      </Box>
      <Box sx={styles.searchRow}>
        <SearchField
          placeholder="Search by projects or folders..."
          value={keyword}
          sx={styles.searchField}
          onChange={(value: string) => setKeyword(value)}
        />
        {/* There is one map view, on the dashboard. The switch's Map segment hands it the folder:
            the dashboard opens with the folder selected in its rail and only its projects on
            the map. */}
        <ProjectsMapToggle
          value="projects"
          onChange={(view) => {
            if (view === 'map') openFolderOnMap();
          }}
        />
      </Box>
      {/* The same controls the dashboard shows, minus the Unassigned filter: inside a
          folder every project is assigned, so those two would select everything or nothing. */}
      <ProjectsControls
        title={folderDetails?.name || 'Projects'}
        sortFilter={sortFilter}
        onSortChange={setSortFilter}
        orderFilter={orderFilter}
        onOrderFilterChange={setOrderFilter}
        filterOptions={FOLDER_ORDER_SORT_FILTER_OPTIONS}
        onOrderFilterOpen={undefined}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      <Box sx={{ ...(loading ? styles.projectsContainer : {}) }}>
        {/* Loading state */}
        {loading && (
          <Box sx={styles.circularProgressContainer}>
            <CircularProgress
              size={50}
            />
          </Box>
        )}

        {/* Render projects */}
        {!loading && visibleProjects.length > 0 && (
          <Box sx={viewMode === 'cards' ? styles.gridList : styles.rowList} ref={listRef}>
            {visibleProjects.map((project: IProject) => (viewMode === 'cards' ? (
              <ProjectListItem
                project={project}
                key={project._id}
                isInsideFolder
                handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                isSelected={project._id === mapPickToReveal}
              />
            ) : (
              <ProjectListRow
                project={project}
                key={project._id}
                isInsideFolder
                handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                isSelected={project._id === mapPickToReveal}
              />
            )))}
          </Box>
        )}

        {nothingMatched && (
          <Box sx={styles.notFound}>
            <Typography variant="h1">{notFoundMessage}</Typography>
          </Box>
        )}

        {/* No projects in the folder yet */}
        {folderIsEmpty && (
          <Box sx={styles.noProjectsContainer}>
            <PlaceOutlinedIcon sx={styles.noProjectsIcon} />
            <Box sx={styles.noProjectsTextContainer}>
              <Typography variant="h3">
                There are no projects to show.
              </Typography>
              <Typography variant="body1">
                Click on the button below to start adding projects.
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={handleMoveProjectBulkOpen}>
              Add Projects
            </Button>
          </Box>
        )}
      </Box>
      <MoveProjectBulkDialog
        open={showMoveProjectBulkDialog}
        onClose={() => {
          setShowMoveProjectBulkDialog(false);
          getProjectsInFolder(folderId);
        }}
        availableProjects={availableProjects}
        loadingAvailableProjects={loadingAvailableProjects}
      />
    </Box>
  );
};

export default FolderDetails;
